import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiServices';
import {
  Container, Row, Col, Card, Button, Form, Alert, Spinner, Table, Badge, Pagination, Accordion, Modal, ProgressBar
} from 'react-bootstrap';
import { 
  FaCloudUploadAlt, FaArrowLeft, FaFilter, FaList, FaSave, FaVolumeUp, 
  FaImage, FaCheckCircle, FaTimesCircle, FaPlus, FaTrash, FaSearch, FaUndo
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const IMAGE_MAX_WIDTH = 1280;
const IMAGE_QUALITY = 0.7;
const AUDIO_SAMPLE_RATE = 22050;
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tif', 'tiff', 'heic', 'heif', 'avif'];
const AUDIO_EXTENSIONS = ['mp3', 'wav', 'aac', 'm4a', 'ogg', 'oga', 'opus', 'webm', 'flac', 'wma'];

const getBaseFileName = (name = 'compressed') => name.replace(/\.[^.]+$/, '');
const getFileExtension = (name = '') => name.split('.').pop()?.toLowerCase() || '';
const formatSizeKB = (bytes) => (bytes / 1024).toFixed(2);

const getFileKind = (file) => {
  const mimeType = file.type || '';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('audio/')) return 'audio';

  const extension = getFileExtension(file.name);
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  if (AUDIO_EXTENSIONS.includes(extension)) return 'audio';

  return 'other';
};

const getAudioMimeType = () => {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
    return '';
  }

  const candidates = [
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
  ];

  return candidates.find(type => window.MediaRecorder.isTypeSupported(type)) || '';
};

const compressImage = (file, onProgress = () => {}) => {
  return new Promise((resolve, reject) => {
    console.log(`[${file.name}] Original Size KB:`, formatSizeKB(file.size));
    onProgress(5);

    const img = new window.Image();
    const reader = new FileReader();

    reader.onerror = reject;
    reader.onload = (e) => {
      onProgress(35);
      img.src = e.target.result;
    };

    img.onerror = reject;
    img.onload = () => {
      onProgress(60);
      const canvas = document.createElement('canvas');
      const scale = Math.min(1, IMAGE_MAX_WIDTH / img.width);

      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file);
        return;
      }

      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.log(`[${file.name}] Compressed Size KB:`, formatSizeKB(file.size));
            onProgress(100);
            resolve(file);
            return;
          }

          const compressedFile = new File([blob], `${getBaseFileName(file.name)}.jpg`, {
            type: 'image/jpeg',
          });

          console.log(`[${file.name}] Compressed Size KB:`, formatSizeKB(compressedFile.size));
          console.log(
            `[${file.name}] Reduced By (%):`,
            (((file.size - compressedFile.size) / file.size) * 100).toFixed(2) + '%'
          );

          onProgress(100);
          resolve(compressedFile);
        },
        'image/jpeg',
        IMAGE_QUALITY
      );
    };

    reader.readAsDataURL(file);
  });
};

const compressAudio = async (file, onProgress = () => {}) => {
  console.log(`[${file.name}] Original Size KB:`, formatSizeKB(file.size));
  onProgress(5);

  const mimeType = getAudioMimeType();
  if (!mimeType) {
    console.log(`[${file.name}] Compressed Size KB:`, formatSizeKB(file.size));
    console.log(`[${file.name}] Reduced By (%):`, '0.00%');
    onProgress(100);
    return file;
  }

  let audioContext;

  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass || !window.MediaRecorder) {
      console.log(`[${file.name}] Compressed Size KB:`, formatSizeKB(file.size));
      console.log(`[${file.name}] Reduced By (%):`, '0.00%');
      onProgress(100);
      return file;
    }

    const arrayBuffer = await file.arrayBuffer();
    audioContext = new AudioContextClass({ sampleRate: AUDIO_SAMPLE_RATE });
    if (audioContext.state === 'suspended' && typeof audioContext.resume === 'function') {
      await audioContext.resume();
    }

    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
    onProgress(20);
    const source = audioContext.createBufferSource();
    const destination = audioContext.createMediaStreamDestination();
    const chunks = [];
    const stopDelayMs = Math.max(1000, Math.ceil(audioBuffer.duration * 1000) + 500);
    const progressState = { value: 20 };
    const progressTimer = setInterval(() => {
      progressState.value = Math.min(95, progressState.value + 3);
      onProgress(progressState.value);
    }, 200);

    source.buffer = audioBuffer;
    source.connect(destination);

    const recordedBlob = await new Promise((resolve, reject) => {
      const recorder = new MediaRecorder(destination.stream, { mimeType });
      let stopTimer = null;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstart = () => {
        console.log(`[${file.name}] Audio compression started with mime:`, mimeType);
        onProgress(35);
      };

      recorder.onerror = (event) => reject(event.error || new Error('Audio compression failed'));
      recorder.onstop = () => {
        if (stopTimer) {
          clearTimeout(stopTimer);
        }
        clearInterval(progressTimer);
        const blob = new Blob(chunks, { type: mimeType });
        resolve(blob);
      };

      source.onended = () => {
        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
      };

      recorder.start();
      source.start(0);
      stopTimer = setTimeout(() => {
        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
      }, stopDelayMs);
    });

    await audioContext.close();

    if (!recordedBlob || recordedBlob.size === 0) {
      console.log(`[${file.name}] Compressed Size KB:`, formatSizeKB(file.size));
      console.log(`[${file.name}] Reduced By (%):`, '0.00%');
      onProgress(100);
      return file;
    }

    const ext = mimeType.includes('ogg') ? 'ogg' : 'webm';
    const compressedFile = new File([recordedBlob], `${getBaseFileName(file.name)}.${ext}`, {
      type: mimeType,
    });

    console.log(`[${file.name}] Compressed Size KB:`, formatSizeKB(compressedFile.size));
    console.log(
      `[${file.name}] Reduced By (%):`,
      (((file.size - compressedFile.size) / file.size) * 100).toFixed(2) + '%'
    );

    onProgress(100);
    return compressedFile;
  } catch (error) {
    console.warn('Audio compression failed, using original file.', error);
    console.log(`[${file.name}] Compressed Size KB:`, formatSizeKB(file.size));
    console.log(`[${file.name}] Reduced By (%):`, '0.00%');
    onProgress(100);
    return file;
  } finally {
    if (audioContext && typeof audioContext.close === 'function') {
      try {
        await audioContext.close();
      } catch (closeError) {
        console.warn('Failed to close audio context after compression.', closeError);
      }
    }
  }
};

const compressFile = async (file, onProgress = () => {}) => {
  if (!file) return file;

  const kind = getFileKind(file);

  if (kind === 'image') {
    return compressImage(file, onProgress);
  }

  if (kind === 'audio') {
    return compressAudio(file, onProgress);
  }

  onProgress(100);
  return file;
};

const UploadJLPTListeningQuiz = () => {
  const navigate = useNavigate();

  // Metadata State
  const [exams, setExams] = useState([]);
  const [testNames, setTestNames] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [questionTypes, setQuestionTypes] = useState([]);
  const [categories, setCategories] = useState([]);

  // Filter/Selection State
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTestName, setSelectedTestName] = useState('');
  const [selectedQuestionType, setSelectedQuestionType] = useState('');
  const [availableCategories, setAvailableCategories] = useState([]);
  const [searchedFilters, setSearchedFilters] = useState(null);

  // Questions List State
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 6;

  // Form State
  const [formData, setFormData] = useState({
    questionText: '',
    text_explanation: '',
    audio_url: null,
    audio_explanation: null,
    question_image: null,
    image_explanation: null
  });
  const [filePreviews, setFilePreviews] = useState({
    audio_url: null,
    audio_explanation: null,
    question_image: null,
    image_explanation: null
  });
  const [compressionState, setCompressionState] = useState({
    active: false,
    field: '',
    label: '',
    progress: 0
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Option Modal State
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [optionFormData, setOptionFormData] = useState({
    optionText: '', isCorrect: false, optionDescription: ''
  });
  const [optionSubmitting, setOptionSubmitting] = useState(false);

  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [optionToDelete, setOptionToDelete] = useState(null);

  // Delete Question Modal State
  const [showDeleteQuestionModal, setShowDeleteQuestionModal] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);
  const [questionDeleting, setQuestionDeleting] = useState(false);

  // File input refs to clear them after submit
  const fileInputRefs = {
    audio_url: React.useRef(null),
    audio_explanation: React.useRef(null),
    question_image: React.useRef(null),
    image_explanation: React.useRef(null)
  };

  useEffect(() => {
    fetchMetadata();
  }, []);

  useEffect(() => {
    return () => {
      Object.values(filePreviews).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [filePreviews]);

  useEffect(() => {
    if (searchedFilters) {
      fetchQuestions(page, searchedFilters);
    }
  }, [page]);

  const fetchMetadata = async () => {
    try {
      const [examRes, testRes, mapRes, qtRes, catRes] = await Promise.all([
        apiService.get('/api/quizTypes'),
        apiService.get('/api/test-names'),
        apiService.get('/api/question-type-mappings'),
        apiService.get('/api/question-type-masters'),
        apiService.get('/api/categories')
      ]);

      if (examRes.success) setExams(examRes.message?.quizTypes || []);
      if (testRes.success) setTestNames(testRes.message?.testNames || []);
      if (mapRes.success) setMappings(mapRes.message?.questionTypeMappings || []);
      if (qtRes.success) setQuestionTypes(qtRes.message?.questionTypeMasters || qtRes.message?.questionTypes || []);
      
      if (catRes.success && Array.isArray(catRes.message?.categories)) {
        setCategories(catRes.message.categories);
      }
    } catch (err) {
      console.error("Error fetching metadata", err);
      setError("Failed to load initial data.");
    }
  };

  const fetchExamDetails = async (examId) => {
    try {
      const response = await apiService.get(`/api/quizTypes/${examId}/details`);
      if (response?.success && response?.message?.quizDetails?.categories?.categoryList) {
        const cats = response.message.quizDetails.categories.categoryList;
        setAvailableCategories(cats);
        const listeningCat = cats.find(c => c.category_name?.toLowerCase().includes('listening'));
        if (listeningCat) {
          setSelectedCategory(listeningCat.category_id);
        } else if (cats.length > 0) {
          setSelectedCategory(cats[0].category_id);
        }
      } else {
        setAvailableCategories([]);
        setSelectedCategory('');
      }
    } catch (err) {
      console.error("Error fetching exam details", err);
      setAvailableCategories([]);
      setSelectedCategory('');
    }
  };

  const fetchQuestions = async (pageToFetch = page, activeFilters = searchedFilters) => {
    if (!activeFilters) return;
    const { selectedExam: exam, selectedCategory: category, selectedTestName: testName, selectedQuestionType: questionType } = activeFilters;
    if (!category) return;

    try {
      setQuestionsLoading(true);

      // Try filter API if all specific filters are selected
      if (exam && testName && questionType && category) {
        const params = {
          page: pageToFetch,
          size: pageSize,
          category_id: category,
          exam_id: exam,
          test_name_id: testName,
          question_type_id: questionType
        };

        const response = await apiService.get('/api/categories/questions/filter', params);
        if (response.success && response.message?.questions && response.message.questions.length > 0) {
          setQuestions(response.message.questions);
          const count = response.message?.totalElements || response.message?.count || 0;
          if (count > 0) {
              setTotalPages(Math.ceil(count / pageSize));
          } else {
              setTotalPages(pageToFetch + (response.message.questions.length < pageSize ? 1 : 2));
          }
        } else {
          setQuestions([]);
          setTotalPages(1);
        }
      } else {
        setQuestions([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error(err);
      setQuestions([]);
      setTotalPages(1);
      // Don't show error for questions fetch to keep UI clean, just log
    } finally {
      setQuestionsLoading(false);
    }
  };

  const handleSearch = () => {
    if (!selectedExam || !selectedCategory || !selectedTestName || !selectedQuestionType) {
      setError("Please select Exam, Category, Test Name, and Question Type first.");
      return;
    }
    setError(null);
    const filters = {
      selectedExam,
      selectedCategory,
      selectedTestName,
      selectedQuestionType
    };
    setSearchedFilters(filters);
    if (page === 0) {
      fetchQuestions(0, filters);
    } else {
      setPage(0);
    }
  };

  const handleResetFilters = () => {
    setSelectedExam('');
    setSelectedCategory('');
    setSelectedTestName('');
    setSelectedQuestionType('');
    setAvailableCategories([]);
    setPage(0);
    setSearchedFilters(null);
    setQuestions([]);
    setError(null);
  };

  const handleFileChange = async (e, key) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const labels = {
        audio_url: 'Main Audio',
        audio_explanation: 'Explanation Audio',
        question_image: 'Question Image',
        image_explanation: 'Explanation Image'
      };

      try {
        setCompressionState({
          active: true,
          field: key,
          label: labels[key] || 'File',
          progress: 1
        });

        const compressedFile = await compressFile(file, (progress) => {
          setCompressionState(prev => ({
            ...prev,
            progress: Math.max(prev.progress, Math.min(100, Math.round(progress)))
          }));
        });

        const previewUrl = URL.createObjectURL(compressedFile);
        setFilePreviews(prev => {
          if (prev[key]) URL.revokeObjectURL(prev[key]);
          return { ...prev, [key]: previewUrl };
        });
        setFormData(prev => ({ ...prev, [key]: compressedFile }));
      } catch (compressionError) {
        console.warn('File compression failed, using original file.', compressionError);
        const previewUrl = URL.createObjectURL(file);
        setFilePreviews(prev => {
          if (prev[key]) URL.revokeObjectURL(prev[key]);
          return { ...prev, [key]: previewUrl };
        });
        setFormData(prev => ({ ...prev, [key]: file }));
      } finally {
        setCompressionState(prev => ({
          ...prev,
          progress: 100
        }));
        window.setTimeout(() => {
          setCompressionState({
            active: false,
            field: '',
            label: '',
            progress: 0
          });
        }, 300);
      }
    }
  };

  const resetUploadForm = () => {
    setFormData({
      questionText: '',
      text_explanation: '',
      audio_url: null,
      audio_explanation: null,
      question_image: null,
      image_explanation: null
    });

    setFilePreviews(prev => {
      Object.values(prev).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      return {
        audio_url: null,
        audio_explanation: null,
        question_image: null,
        image_explanation: null
      };
    });

    Object.values(fileInputRefs).forEach(ref => {
      if (ref.current) ref.current.value = '';
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (compressionState.active) {
      setError('Compression is in progress. Please wait.');
      return;
    }
    if (!selectedExam || !selectedTestName || !selectedQuestionType || !selectedCategory) {
      setError("Please select Exam, Category, Test Name, and Question Type first.");
      return;
    }
    if (!formData.questionText) {
        setError("Question text is required.");
        return;
    }

    // Find question_type_mapping_id
    // Mapping logic: find mapping where test_type_id == selectedTestName AND question_type_master_id == selectedQuestionType
    const mapping = mappings.find(m => 
      Number(m.test_type_id) === Number(selectedTestName) && 
      Number(m.question_type_master_id) === Number(selectedQuestionType)
    );

    if (!mapping) {
      setError("No valid mapping found for this Test Name and Question Type. Please configure mappings first.");
      return;
    }

    try {
      setFormSubmitting(true);
      setError(null);
      setSuccessMsg('');

      const data = new FormData();
      data.append('questionText', formData.questionText);
      data.append('text_explanation', formData.text_explanation);
      data.append('categoryId', selectedCategory);
      data.append('exam_id', selectedExam);
      data.append('question_type_mapping_id', mapping.id || mapping.question_type_mapping_id);

      if (formData.audio_url) data.append('audio_url', formData.audio_url);
      if (formData.audio_explanation) data.append('audio_explanation', formData.audio_explanation);
      if (formData.question_image) data.append('question_image', formData.question_image);
      if (formData.image_explanation) data.append('image_explanation', formData.image_explanation);

      const response = await apiService.post('/api/questions/with-assets', data);

      if (response.success) {
        setSuccessMsg("Question uploaded successfully!");
        resetUploadForm();
        fetchQuestions(); // Refresh list
      } else {
        setError(response.message || "Failed to upload question.");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Error uploading question.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const openOptionModal = (questionId) => {
    setSelectedQuestionId(questionId);
    setOptionFormData({ optionText: '', isCorrect: false, optionDescription: '' });
    setShowOptionModal(true);
  };

  const handleOptionSubmit = async (e) => {
    e.preventDefault();
    if (!optionFormData.optionText) {
      setError("Option text is required.");
      return;
    }

    try {
      setOptionSubmitting(true);
      setError(null);
      setSuccessMsg('');

      const payload = {
        optionText: optionFormData.optionText,
        isCorrect: optionFormData.isCorrect ? "true" : "false",
        optionDescription: optionFormData.optionDescription,
        questionId: selectedQuestionId
      };

      const response = await apiService.post('/api/options', payload);

      if (response.success) {
        setSuccessMsg("Option added successfully!");
        setShowOptionModal(false);
        fetchQuestions(); // Refresh list to show new option
      } else {
        setError(response.message || "Failed to add option.");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Error adding option.");
    } finally {
      setOptionSubmitting(false);
    }
  };

  const handleDeleteOptionClick = (optionId) => {
    setOptionToDelete(optionId);
    setShowDeleteModal(true);
  };

  const confirmDeleteOption = async () => {
    if (!optionToDelete) return;

    try {
      setError(null);
      const response = await apiService.delete('/api/options', {
        data: { optionId: optionToDelete }
      });
      if (response.success) {
        setSuccessMsg("Option deleted successfully!");
        setShowDeleteModal(false);
        fetchQuestions(); // Refresh list to reflect deleted option
      } else {
        setError(response.message || "Failed to delete option.");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Error deleting option.");
    } finally {
      setOptionToDelete(null);
    }
  };

  const handleDeleteQuestionClick = (questionId) => {
    setQuestionToDelete(questionId);
    setShowDeleteQuestionModal(true);
  };

  const confirmDeleteQuestion = async () => {
    if (!questionToDelete) return;

    try {
      setQuestionDeleting(true);
      setError(null);
      const response = await apiService.delete('/api/questions', {
        data: { questionId: questionToDelete }
      });
      if (response.success) {
        setSuccessMsg("Question deleted successfully!");
        setShowDeleteQuestionModal(false);
        fetchQuestions(); // Refresh list
      } else {
        setError(response.message || "Failed to delete question.");
      }
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Error deleting question.");
    } finally {
      setQuestionDeleting(false);
      setQuestionToDelete(null);
    }
  };

  // Filter dropdown options
  const filteredTests = testNames.filter(t => Number(t.exam_id) === Number(selectedExam));
  
  // Filter Question Types based on Mappings for the selected Test Name
  const availableQuestionTypeIds = mappings
    .filter(m => Number(m.test_type_id) === Number(selectedTestName))
    .map(m => Number(m.question_type_master_id));
  
  const filteredQuestionTypes = questionTypes.filter(qt => 
    availableQuestionTypeIds.includes(Number(qt.id || qt.question_type_master_id))
  );

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button variant="outline-secondary" className="mb-2" onClick={() => navigate('/upload-data')}>
            <FaArrowLeft className="me-2" /> Back to Tiles
          </Button>
          <h2 className="mb-0">
            <FaCloudUploadAlt className="me-2 text-primary" />
            Upload Listening Question
          </h2>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert variant="success" dismissible onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      <Row>
        {/* Filters & Upload Form */}
        <Col lg={4} className="mb-4">
          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light fw-bold"><FaFilter className="me-2" /> Configuration</Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Exam (Quiz Type)</Form.Label>
                <Form.Select value={selectedExam} onChange={e => {
                    const val = e.target.value;
                    setSelectedExam(val);
                    setSelectedCategory('');
                    setSelectedTestName('');
                    setSelectedQuestionType('');
                    setAvailableCategories([]);
                    if (val) fetchExamDetails(val);
                }}>
                  <option value="">Select Exam</option>
                  {exams.map(e => (
                    <option key={e.quiz_type_id} value={e.quiz_type_id}>{e.quiz_type_name}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Category</Form.Label>
                <Form.Select 
                  value={selectedCategory} 
                  onChange={e => setSelectedCategory(e.target.value)}
                  disabled={!selectedExam || availableCategories.length === 0}
                >
                  <option value="">Select Category</option>
                  {availableCategories.map(c => (
                    <option key={c.category_id} value={c.category_id}>
                      {c.category_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Test Name</Form.Label>
                <Form.Select 
                  value={selectedTestName} 
                  onChange={e => {
                      setSelectedTestName(e.target.value);
                      setSelectedQuestionType('');
                  }}
                  disabled={!selectedExam}
                >
                  <option value="">Select Test Name</option>
                  {filteredTests.map(t => (
                    <option key={t.test_name_id || t.id} value={t.test_name_id || t.id}>{t.test_name}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Question Type</Form.Label>
                <Form.Select 
                  value={selectedQuestionType} 
                  onChange={e => setSelectedQuestionType(e.target.value)}
                  disabled={!selectedTestName}
                >
                  <option value="">Select Question Type</option>
                  {filteredQuestionTypes.map(qt => (
                    <option key={qt.id || qt.question_type_master_id} value={qt.id || qt.question_type_master_id}>
                      {qt.name}
                    </option>
                  ))}
                </Form.Select>
                {selectedTestName && filteredQuestionTypes.length === 0 && (
                   <Form.Text className="text-danger">No question types mapped to this test. Check mappings.</Form.Text>
                )}
              </Form.Group>

              <div className="d-flex gap-2 mt-4">
                <Button 
                  variant="primary" 
                  className="flex-grow-1 d-flex align-items-center justify-content-center" 
                  onClick={handleSearch}
                  disabled={!selectedExam || !selectedCategory || !selectedTestName || !selectedQuestionType}
                >
                  <FaSearch className="me-2" /> Search
                </Button>
                <Button 
                  variant="outline-secondary" 
                  className="d-flex align-items-center justify-content-center"
                  onClick={handleResetFilters}
                >
                  <FaUndo className="me-2" /> Reset
                </Button>
              </div>
            </Card.Body>
          </Card>

          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white fw-bold">Add New Question</Card.Header>
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                {compressionState.active && (
                  <Alert variant="info" className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>Compression is in progress</strong>
                      <span>{compressionState.progress}%</span>
                    </div>
                    <div className="mb-2 small text-muted">
                      Please wait while {compressionState.label} is being compressed.
                    </div>
                    <ProgressBar now={compressionState.progress} animated striped />
                  </Alert>
                )}

                <Form.Group className="mb-3">
                  <Form.Label>Question Text *</Form.Label>
                  <Form.Control 
                    as="textarea" rows={2} 
                    value={formData.questionText}
                    onChange={e => setFormData({...formData, questionText: e.target.value})}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Text Explanation</Form.Label>
                  <Form.Control 
                    as="textarea" rows={2}
                    value={formData.text_explanation}
                    onChange={e => setFormData({...formData, text_explanation: e.target.value})}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Main Audio</Form.Label>
                  <Form.Control type="file" accept="audio/*" ref={fileInputRefs.audio_url} onChange={e => handleFileChange(e, 'audio_url')} disabled={compressionState.active || formSubmitting} />
                  {filePreviews.audio_url && (
                    <div className="mt-2 p-2 border rounded bg-light">
                      <small className="text-muted d-block mb-1">Preview</small>
                      <audio controls src={filePreviews.audio_url} className="w-100" />
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Explanation Audio</Form.Label>
                  <Form.Control type="file" accept="audio/*" ref={fileInputRefs.audio_explanation} onChange={e => handleFileChange(e, 'audio_explanation')} disabled={compressionState.active || formSubmitting} />
                  {filePreviews.audio_explanation && (
                    <div className="mt-2 p-2 border rounded bg-light">
                      <small className="text-muted d-block mb-1">Preview</small>
                      <audio controls src={filePreviews.audio_explanation} className="w-100" />
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Question Image</Form.Label>
                  <Form.Control type="file" accept="image/*" ref={fileInputRefs.question_image} onChange={e => handleFileChange(e, 'question_image')} disabled={compressionState.active || formSubmitting} />
                  {filePreviews.question_image && (
                    <div className="mt-2 p-2 border rounded bg-light">
                      <small className="text-muted d-block mb-1">Preview</small>
                      <img
                        src={filePreviews.question_image}
                        alt="Question preview"
                        className="img-fluid rounded border"
                        style={{ maxHeight: '180px' }}
                      />
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Explanation Image</Form.Label>
                  <Form.Control type="file" accept="image/*" ref={fileInputRefs.image_explanation} onChange={e => handleFileChange(e, 'image_explanation')} disabled={compressionState.active || formSubmitting} />
                  {filePreviews.image_explanation && (
                    <div className="mt-2 p-2 border rounded bg-light">
                      <small className="text-muted d-block mb-1">Preview</small>
                      <img
                        src={filePreviews.image_explanation}
                        alt="Explanation preview"
                        className="img-fluid rounded border"
                        style={{ maxHeight: '180px' }}
                      />
                    </div>
                  )}
                </Form.Group>

                <Button variant="primary" type="submit" className="w-100" disabled={formSubmitting || compressionState.active || !selectedQuestionType}>
                  {formSubmitting ? <Spinner size="sm" animation="border" /> : <><FaSave className="me-2" /> Upload Question</>}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        {/* Questions List */}
        <Col lg={8}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3">
              <h5 className="mb-0"><FaList className="me-2" /> Existing Questions</h5>
              <Badge bg="info">Category ID: {selectedCategory || 'None'}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {questionsLoading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
              ) : questions.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    {selectedCategory ? "No questions found for this category/selection." : "Select an Exam and Category to view questions."}
                </div>
              ) : (
                <>
                  <div className="p-3 bg-light">
                    <Accordion alwaysOpen>
                      {questions.map((q, idx) => (
                        <Accordion.Item eventKey={q.question_id.toString()} key={q.question_id} className="mb-3 shadow-sm border">
                          <Accordion.Header>
                            <div className="d-flex justify-content-between align-items-center w-100 pe-3">
                              <strong>Q{idx + 1 + (page * pageSize)}: Question ID {q.question_id}</strong>
                              <span className="text-muted small">{new Date(q.created_at).toLocaleDateString()}</span>
                            </div>
                          </Accordion.Header>
                          <Accordion.Body className="bg-white">
                          <Row>
                            <Col md={12} className="mb-3 d-flex justify-content-between align-items-start">
                               <h5 className="text-dark mb-0 pe-3">{q.question_text}</h5>
                               <Button 
                                 variant="outline-danger" 
                                 size="sm" 
                                 onClick={() => handleDeleteQuestionClick(q.question_id)}
                                 className="text-nowrap"
                               >
                                 <FaTrash className="me-1" /> Delete Question
                               </Button>
                            </Col>
                            
                            <Col md={12} className="mb-3">
                               <div className="d-flex gap-3 flex-wrap">
                                 {q.audio_url && (
                                   <div className="flex-grow-1" style={{minWidth: '200px'}}>
                                     <strong className="d-block small text-muted mb-1"><FaVolumeUp /> Question Audio</strong>
                                     <audio controls src={q.audio_url} className="w-100" style={{height: '30px'}} />
                                   </div>
                                 )}
                                 {q.question_image && (
                                   <div>
                                      <strong className="d-block small text-muted mb-1"><FaImage /> Question Image</strong>
                                      <img src={q.question_image} alt="Question" className="img-fluid rounded border" style={{maxHeight: '150px'}} />
                                   </div>
                                 )}
                               </div>
                            </Col>

                            <Col md={12}>
                              <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                                <h6 className="fw-bold text-secondary mb-0">Options</h6>
                                <Button variant="outline-primary" size="sm" onClick={() => openOptionModal(q.question_id)}>
                                  <FaPlus className="me-1" /> Add Option
                                </Button>
                              </div>
                              <div className="list-group mb-3">
                                {q.options && q.options.length > 0 ? q.options.map(opt => (
                                  <div key={opt.option_id} className={`list-group-item ${opt.is_correct ? 'list-group-item-success' : ''}`}>
                                    <div className="d-flex w-100 justify-content-between align-items-center">
                                      <span>
                                        {opt.is_correct ? <FaCheckCircle className="text-success me-2"/> : <FaTimesCircle className="text-muted me-2" />}
                                        {opt.option_text}
                                      </span>
                                      <div className="d-flex align-items-center gap-3">
                                        {opt.is_correct && <Badge bg="success">Correct</Badge>}
                                        <Button 
                                          variant="outline-danger" 
                                          size="sm" 
                                          onClick={() => handleDeleteOptionClick(opt.option_id)}
                                          title="Delete Option"
                                        >
                                          <FaTrash />
                                        </Button>
                                      </div>
                                    </div>
                                    {opt.option_description && <small className="text-muted d-block mt-1 ps-4">{opt.option_description}</small>}
                                  </div>
                                )) : <div className="text-muted fst-italic">No options available.</div>}
                              </div>
                            </Col>

                            {(q.text_explanation || q.audio_explanation || q.image_explanation) && (
                              <Col md={12}>
                                 <div className="p-3 bg-light rounded border-start border-4 border-info">
                                   <h6 className="text-info fw-bold">Explanation</h6>
                                   {q.text_explanation && <p className="small mb-2">{q.text_explanation}</p>}
                                   
                                   <div className="d-flex gap-3 flex-wrap align-items-center">
                                     {q.audio_explanation && (
                                       <div>
                                         <small className="text-muted d-block"><FaVolumeUp /> Audio Exp.</small>
                                         <audio controls src={q.audio_explanation} style={{height: '30px', maxWidth: '250px'}} />
                                       </div>
                                     )}
                                     {q.image_explanation && (
                                        <div>
                                          <small className="text-muted d-block"><FaImage /> Image Exp.</small>
                                          <img src={q.image_explanation} alt="Explanation" className="img-thumbnail" style={{height: '80px'}} />
                                        </div>
                                     )}
                                   </div>
                                 </div>
                              </Col>
                            )}
                          </Row>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </div>
                  <div className="d-flex justify-content-center py-3">
                    <Pagination>
                        <Pagination.Prev onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} />
                        <Pagination.Item active>{page + 1}</Pagination.Item>
                        <Pagination.Next onClick={() => setPage(p => p + 1)} disabled={questions.length < pageSize} />
                    </Pagination>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Add Option Modal */}
      <Modal show={showOptionModal} onHide={() => setShowOptionModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Option</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleOptionSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Option Text *</Form.Label>
              <Form.Control 
                type="text"
                value={optionFormData.optionText}
                onChange={e => setOptionFormData({...optionFormData, optionText: e.target.value})}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Option Description</Form.Label>
              <Form.Control 
                as="textarea" rows={2}
                value={optionFormData.optionDescription}
                onChange={e => setOptionFormData({...optionFormData, optionDescription: e.target.value})}
                placeholder="Optional explanation for this option"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check 
                type="switch"
                id="is-correct-switch"
                label="Is Correct Option?"
                checked={optionFormData.isCorrect}
                onChange={e => setOptionFormData({...optionFormData, isCorrect: e.target.checked})}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowOptionModal(false)}>Cancel</Button>
            <Button variant="primary" type="submit" disabled={optionSubmitting}>
              {optionSubmitting ? <Spinner size="sm" animation="border" /> : "Save Option"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this option?</p>
          <p className="text-muted small">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteOption}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Question Confirmation Modal */}
      <Modal show={showDeleteQuestionModal} onHide={() => setShowDeleteQuestionModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this question?</p>
          <p className="text-danger small">This action will also delete all associated options and cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteQuestionModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteQuestion} disabled={questionDeleting}>
            {questionDeleting ? <Spinner size="sm" animation="border" /> : "Delete Question"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default UploadJLPTListeningQuiz;
